// @ts-strict-ignore
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";

import { ModularOnboardingModalContext } from "@clinic/shared/context/ModularOnboardingModalContext";
import { l } from "@clinic/shared/locales/i18n";
import { useStepDetails } from "@shared/components/ModularOnboardingModalProvider/useStepDetails";
import { useStoreSetupQuery } from "@shared/data/queries/StoreSetupQuery/StoreSetup.query";
import { useSharedGlobalConfig } from "@shared/hooks/useSharedGlobalConfig/useSharedGlobalConfig";
import { ModularStoreSetupSteps } from "@shared/types/ModularStoreSetupSteps";

interface Props {
  children: ReactNode;
}

const ModularOnboardingModalProvider = ({ children }: Props) => {
  const { t } = useTranslation();

  const { storePlatform, isUnauthenticated } = useSharedGlobalConfig();

  const { data, loading } = useStoreSetupQuery({
    variables: { storePlatform },
    skip: isUnauthenticated,
  });

  const stepData = useStepDetails(data?.currentStore?.storeSetup?.stepDetails);

  const { currentStep, currentStepNumber, nextStep, previousStep, totalStepCount } = stepData;
  const { marginType, currentSetupPage } = data?.currentStore || {};

  const nextStepText = (marginTypeOverride: "never" | "variable") => {
    const step = nextStep[`${marginTypeOverride || marginType}Margin`];
    const stepName = t(l.onboarding.certUpExperiment.stepName[step]);

    return t(l.onboarding.nextStepButton, {
      stepName,
    });
  };

  return (
    <ModularOnboardingModalContext
      value={{
        loading,
        currentStepNumber,
        totalStepCount,
        currentSetupPage,
        currentStep: currentStep || ModularStoreSetupSteps.CERTIFICATE,
        nextStep: nextStep || {
          neverMargin: ModularStoreSetupSteps.CERTIFICATE,
          variableMargin: ModularStoreSetupSteps.CERTIFICATE,
        },
        isSetupComplete: data?.currentStore?.storeSetup?.isSetupComplete,
        nextStepText,
        marginType,
        previousStep,
        canGoBack: true,
        stepDetails: data?.currentStore?.storeSetup?.stepDetails,
        goBack: stepData.goBack,
        goNext: stepData.goNext,
      }}
    >
      {children}
    </ModularOnboardingModalContext>
  );
};

export { ModularOnboardingModalProvider };
